import React from 'react';

import { UnplugIcon } from 'lucide-react';
import { useAccount } from 'wagmi';

import useChainByCurrency from '@/hooks/useChainByCurrency';
import { ConnectedBtcWallet, useBtcWallet } from '@/networks/btc-like/btc';
import { ConnectedDogeWallet, useDogeWallet } from '@/networks/btc-like/doge';
import { ConnectedLtcWallet, useLtcWallet } from '@/networks/btc-like/ltc';
import { ConnectedCoinwebWallet, useCoinwebWallet } from '@/networks/coinweb';
import { ConnectedEvmWallet } from '@/networks/evm';
import { ConnectedTronWallet, useTronWallet } from '@/networks/tron';
import { useSwapContext } from '@/pages/Swap/context';
import { cn } from '@/utils';

interface HeaderWalletButtonProps extends React.HTMLAttributes<HTMLDivElement> {}

const HeaderWalletButton = React.forwardRef<HTMLDivElement, HeaderWalletButtonProps>(({ className, ...props }, ref) => {
  const { tokenFrom } = useSwapContext();
  const evmAccount = useAccount();
  const btcAccount = useBtcWallet();
  const tronAccount = useTronWallet();
  const ltcAccount = useLtcWallet();
  const dogeAccount = useDogeWallet();
  const coinwebAccount = useCoinwebWallet();

  const { isBtc, isEvm, isTron, isLtc, isDoge, isCweb } = useChainByCurrency(tokenFrom);

  if (isEvm) {
    return (
      <div ref={ref} {...props}>
        <ConnectedEvmWallet onClick={() => null} className={cn('p-6 sm:px-4 sm:py-2', className)}>
          {!evmAccount.isConnected && (
            <>
              <UnplugIcon size={22} strokeWidth="1.5px" className="lg:hidden" />
              <span className="hidden lg:flex">Connect EVM Wallet</span>
            </>
          )}
        </ConnectedEvmWallet>
      </div>
    );
  }

  if (isBtc) {
    return (
      <div ref={ref} {...props}>
        <ConnectedBtcWallet onClick={() => null} className={cn('p-6 sm:px-4 sm:py-2', className)}>
          {!btcAccount.isConnected && (
            <>
              <UnplugIcon size={22} strokeWidth="1.5px" className="lg:hidden" />
              <span className="hidden lg:flex">Connect BTC Wallet</span>
            </>
          )}
        </ConnectedBtcWallet>
      </div>
    );
  }

  if (isTron) {
    return (
      <div ref={ref} {...props}>
        <ConnectedTronWallet onClick={() => null} className={cn('p-6 sm:px-4 sm:py-2', className)}>
          {!tronAccount.isConnected && (
            <>
              <UnplugIcon size={22} strokeWidth="1.5px" className="lg:hidden" />
              <span className="hidden lg:flex">Connect Tron Wallet</span>
            </>
          )}
        </ConnectedTronWallet>
      </div>
    );
  }

  if (isLtc) {
    return (
      <div ref={ref} {...props}>
        <ConnectedLtcWallet onClick={() => null} className={cn('p-6 sm:px-4 sm:py-2', className)}>
          {!ltcAccount.isConnected && (
            <>
              <UnplugIcon size={22} strokeWidth="1.5px" className="lg:hidden" />
              <span className="hidden lg:flex">Connect Litecoin Wallet</span>
            </>
          )}
        </ConnectedLtcWallet>
      </div>
    );
  }

  if (isDoge) {
    return (
      <div ref={ref} {...props}>
        <ConnectedDogeWallet onClick={() => null} className={cn('p-6 sm:px-4 sm:py-2', className)}>
          {!dogeAccount.isConnected && (
            <>
              <UnplugIcon size={22} strokeWidth="1.5px" className="lg:hidden" />
              <span className="hidden lg:flex">Connect Dogecoin Wallet</span>
            </>
          )}
        </ConnectedDogeWallet>
      </div>
    );
  }

  if (isCweb) {
    return (
      <div ref={ref} {...props}>
        <ConnectedCoinwebWallet onClick={() => null} className={cn('p-6 sm:px-4 sm:py-2', className)}>
          {!coinwebAccount.isConnected && (
            <>
              <UnplugIcon size={22} strokeWidth="1.5px" className="lg:hidden" />
              <span className="hidden lg:flex">Connect Coinweb Wallet</span>
            </>
          )}
        </ConnectedCoinwebWallet>
      </div>
    );
  }

  return null;
});

HeaderWalletButton.displayName = 'HeaderWalletButton';

export default HeaderWalletButton;
